"use client";

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

type RevealProps = {
  children: ReactNode;
  className?: string;
  delayMs?: number;
};

const delayClassNames: Record<number, string> = {
  100: "reveal-delay-100",
  150: "reveal-delay-150",
  200: "reveal-delay-200",
  300: "reveal-delay-300",
};

/**
 * Reveals content when it scrolls into view so long marketing pages feel lighter and more staged.
 *
 * @param children - The content to animate into view.
 * @param className - Optional wrapper classes for layout or additional styling.
 * @param delayMs - Optional delay to stagger the reveal animation.
 * @returns A client-side wrapper that fades and lifts its children into view.
 */
export default function Reveal({ children, className = "", delayMs }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const delayClassName = delayMs ? delayClassNames[delayMs] ?? "" : "";

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      } ${delayClassName} ${className}`}
    >
      {children}
    </div>
  );
}
